import React, {useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import moment from 'moment';
import ListItemText from '@material-ui/core/ListItemText';
import cn from 'classnames';
import DatePicker from 'components/feature/DatePicker';
import {
  getCustomRange,
  setCustomRange
} from 'helpers/localStorage/CustomRangeStorage';
import {
  getStoragePeriod,
  setStoragePeriod
} from 'helpers/localStorage/DatePeriodStorage';
import {setStorageDate} from 'helpers/localStorage/DateStorage';
import DatePickerContainer from 'routes/App/reports/ReportsPageDatepickerModule/styles';
import {ButtonInfo, ButtonPrimary} from 'components/buttons';
import {REPORT_DATEPICKER_INDEX, REPORT_TYPES} from 'helpers/constants/reports';

const ReportsDatepickerModule = ({
  date,
  updateDate,
  updateSelectedDateIndex,
  localStorageObject: {
    storageDateName,
    storageCustomRangeName,
    storageDatePeriodName
  },
  defaultSelectIndex,
  reportType,
  Timezone
}) => {
  const [selectedDate, setSelectedDate] = useState([]);
  const [open, setOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectIndex);
  const [dateValue, setDateValue] = useState([null, null]);
  const prevReportTypeRef = useRef();

  useEffect(() => {
    setDateValue(getCustomRange(storageCustomRangeName) || [null, null]);
  }, []);

  const handleOpen = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const index = getStoragePeriod(storageDatePeriodName);
    if (index !== undefined && index !== null) {
      setSelectedIndex(index);
    } else {
      setSelectedIndex(defaultSelectIndex);
    }
  }, [defaultSelectIndex]);

  const handleListItemClick = (event, index) => {
    updateSelectedDateIndex(index);
    if (index !== 6) {
      setOpen(false);
    }
    setSelectedIndex(index);
    if (storageDatePeriodName && reportType !== REPORT_TYPES.SPECIAL_ORDERS) {
      setStoragePeriod(storageDatePeriodName, index);
    }
    // setOpen(true);
  };

  const setDate = () => {
    if (
      selectedDate.length &&
      selectedDate[0] !== null &&
      selectedDate[1] !== null
    ) {
      updateDate(selectedDate, selectedIndex);
      if (storageDateName && reportType !== REPORT_TYPES.SPECIAL_ORDERS) {
        setStorageDate(storageDateName, selectedDate);
      }
    }
  };

  const updateDatePeriod = (startPeriod, endPeriod) => {
    setSelectedDate([]);
    const periodCurrentDate = [startPeriod, endPeriod];
    updateDate(periodCurrentDate, selectedIndex);
    if (
      storageDateName &&
      storageCustomRangeName &&
      reportType !== REPORT_TYPES.SPECIAL_ORDERS
    ) {
      setStorageDate(storageDateName, periodCurrentDate);
      setCustomRange(storageCustomRangeName, []);
    }
  };

  const datepickerOnChange = (datePickerDate) => {
    setDateValue(datePickerDate);
    if (storageCustomRangeName && reportType !== REPORT_TYPES.SPECIAL_ORDERS) {
      setCustomRange(storageCustomRangeName, datePickerDate);
    }
  };

  const locationDate = () => {
    return moment().utcOffset(Timezone, false).utc();
  };

  useEffect(() => {
    if (
      prevReportTypeRef?.current === REPORT_TYPES.SPECIAL_ORDERS &&
      reportType !== REPORT_TYPES.SPECIAL_ORDERS
    ) {
      setSelectedIndex(REPORT_DATEPICKER_INDEX.LAST_WEEK);
      updateSelectedDateIndex(REPORT_DATEPICKER_INDEX.LAST_WEEK);
      updateDatePeriod(
        locationDate().subtract(1, 'week').startOf('isoWeek').format(),
        locationDate().subtract(1, 'week').endOf('isoWeek').format()
      );
    }
    prevReportTypeRef.current = reportType;
  }, [reportType]);

  return (
    <DatePickerContainer>
      <span>Time period</span>
      <div
        className={cn('Say2EatDrop', {
          withTooltip: reportType === REPORT_TYPES.SPECIAL_ORDERS
        })}
      >
        <FormControl variant='outlined'>
          <Select
            labelId='demo-simple-select-outlined-label'
            id='demo-simple-select-outlined'
            multiple
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              },
              getContentAnchorEl: null
            }}
            value={date}
            renderValue={() => {
              // No stored range means "All period", whatever entry is highlighted
              if (!date?.[0] && !date?.[1]) {
                return 'All period';
              }

              const defaultList = [
                'All period',
                `${moment(date[0]).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`
              ];

              const specialOrdersList = [
                'All period',
                `${moment(date[0]).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`,
                `${moment(date[0]).format('ddd MMM DD yyyy')} - ${moment(
                  date[1]
                ).format('ddd MMM DD yyyy')}`
              ];

              return reportType === REPORT_TYPES.SPECIAL_ORDERS
                ? specialOrdersList[selectedIndex]
                : defaultList[selectedIndex];
            }}
            // label="Custom range"
            open={open}
            onClose={handleOpen}
            onOpen={handleOpen}
            onChange={() => {}}
          >
            <div className='DropDown DropFilterDate'>
              <div className='DropDownHolder'>
                <div className='DropDownList'>
                  <List
                    component='nav'
                    className='DropDownMenu'
                    aria-label='main mailbox folders'
                  >
                    {reportType === REPORT_TYPES.SPECIAL_ORDERS ? (
                      <>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.TODAY
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.TODAY
                            );
                            updateDatePeriod(
                              locationDate().startOf('day').format(),
                              locationDate().endOf('day').format()
                            );
                          }}
                        >
                          <ListItemText primary='Today' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.THIS_WEEK
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.THIS_WEEK
                            );
                            updateDatePeriod(
                              locationDate().startOf('isoWeek').format(),
                              locationDate().endOf('isoWeek').format()
                            );
                          }}
                        >
                          <ListItemText primary='This week' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.NEXT_WEEK
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.NEXT_WEEK
                            );

                            updateDatePeriod(
                              locationDate()
                                .add(1, 'week')
                                .startOf('isoWeek')
                                .format(),
                              locationDate()
                                .add(1, 'week')
                                .endOf('isoWeek')
                                .format()
                            );
                          }}
                        >
                          <ListItemText primary='Next week' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.LAST_WEEK
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.LAST_WEEK
                            );

                            updateDatePeriod(
                              locationDate()
                                .subtract(1, 'week')
                                .startOf('isoWeek')
                                .format(),
                              locationDate()
                                .subtract(1, 'week')
                                .endOf('isoWeek')
                                .format()
                            );
                          }}
                        >
                          <ListItemText primary='Last week' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.THIS_MONTH
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.THIS_MONTH
                            );

                            updateDatePeriod(
                              locationDate().startOf('month').format(),
                              locationDate().endOf('month').format()
                            );
                          }}
                        >
                          <ListItemText primary='This month' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.NEXT_MONTH
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.NEXT_MONTH
                            );

                            updateDatePeriod(
                              locationDate()
                                .add(1, 'month')
                                .startOf('month')
                                .format(),
                              locationDate()
                                .add(1, 'month')
                                .endOf('month')
                                .format()
                            );
                          }}
                        >
                          <ListItemText primary='Next month' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.LAST_MONTH
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.LAST_MONTH
                            );

                            updateDatePeriod(
                              locationDate()
                                .subtract(1, 'month')
                                .startOf('month')
                                .format(),
                              locationDate()
                                .subtract(1, 'month')
                                .endOf('month')
                                .format()
                            );
                          }}
                        >
                          <ListItemText primary='Last month' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex ===
                            REPORT_DATEPICKER_INDEX.CUSTOM_RANGE
                          }
                          onClick={(event) => {
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.CUSTOM_RANGE
                            );
                            setOpen(true);
                            setShowDatePicker(!showDatePicker);
                          }}
                        >
                          <ListItemText primary='Custom Range' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.ALL_PERIOD
                          }
                          onClick={(event) => {
                            updateDatePeriod(null, null);
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.ALL_PERIOD
                            );
                          }}
                        >
                          <ListItemText primary='All period' />
                        </ListItem>
                      </>
                    ) : (
                      <>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.ALL_PERIOD
                          }
                          onClick={(event) => {
                            updateDatePeriod(null, null);
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.ALL_PERIOD
                            );
                          }}
                        >
                          <ListItemText primary='All period' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.TODAY
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.TODAY
                            );
                            updateDatePeriod(
                              locationDate().startOf('day').format(),
                              locationDate().endOf('day').format()
                            );
                          }}
                        >
                          <ListItemText primary='Today' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.THIS_WEEK
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.THIS_WEEK
                            );
                            updateDatePeriod(
                              locationDate().startOf('isoWeek').format(),
                              locationDate().endOf('isoWeek').format()
                            );
                          }}
                        >
                          <ListItemText primary='This week' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.LAST_WEEK
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.LAST_WEEK
                            );

                            updateDatePeriod(
                              locationDate()
                                .subtract(1, 'week')
                                .startOf('isoWeek')
                                .format(),
                              locationDate()
                                .subtract(1, 'week')
                                .endOf('isoWeek')
                                .format()
                            );
                          }}
                        >
                          <ListItemText primary='Last week' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.THIS_MONTH
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.THIS_MONTH
                            );

                            updateDatePeriod(
                              locationDate().startOf('month').format(),
                              locationDate().endOf('month').format()
                            );
                          }}
                        >
                          <ListItemText primary='This month' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex === REPORT_DATEPICKER_INDEX.LAST_MONTH
                          }
                          onClick={(event) => {
                            setShowDatePicker(false);
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.LAST_MONTH
                            );

                            updateDatePeriod(
                              locationDate()
                                .subtract(1, 'month')
                                .startOf('month')
                                .format(),
                              locationDate()
                                .subtract(1, 'month')
                                .endOf('month')
                                .format()
                            );
                          }}
                        >
                          <ListItemText primary='Last month' />
                        </ListItem>
                        <ListItem
                          button
                          selected={
                            selectedIndex ===
                            REPORT_DATEPICKER_INDEX.CUSTOM_RANGE
                          }
                          onClick={(event) => {
                            handleListItemClick(
                              event,
                              REPORT_DATEPICKER_INDEX.CUSTOM_RANGE
                            );
                            setOpen(true);
                            setShowDatePicker(!showDatePicker);
                          }}
                        >
                          <ListItemText primary='Custom Range' />
                        </ListItem>
                      </>
                    )}
                  </List>
                </div>
                {showDatePicker && (
                  <div className='DropDownDatepicker'>
                    <DatePicker
                      setSelectedDate={setSelectedDate}
                      dateValue={dateValue}
                      datepickerOnChange={datepickerOnChange}
                      disableFuture={reportType !== REPORT_TYPES.SPECIAL_ORDERS}
                    />
                    <div className='DropDownActions'>
                      <ButtonPrimary
                        className='ButtonSmall'
                        onClick={() => {
                          setDate();
                          handleOpen();
                        }}
                      >
                        <span>Apply</span>
                      </ButtonPrimary>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Select>
        </FormControl>
        {reportType === REPORT_TYPES.SPECIAL_ORDERS && (
          <ButtonInfo className='Outline ButtonInfoDrop marginLeftXsPlus'>
            <div className='DropInfo DropInfoRight DropInfoRightMob'>
              Select time period of when the orders were placed.
            </div>
          </ButtonInfo>
        )}
      </div>
    </DatePickerContainer>
  );
};

ReportsDatepickerModule.propTypes = {
  date: PropTypes.instanceOf(Array).isRequired,
  updateDate: PropTypes.func.isRequired,
  updateSelectedDateIndex: PropTypes.func,
  localStorageObject: PropTypes.shape({
    storageDateName: PropTypes.string,
    storageCustomRangeName: PropTypes.string,
    storageDatePeriodName: PropTypes.string
  }),
  defaultSelectIndex: PropTypes.number,
  reportType: PropTypes.number,
  Timezone: PropTypes.string
};

ReportsDatepickerModule.defaultProps = {
  updateSelectedDateIndex: () => {},
  localStorageObject: {
    storageDateName: '',
    storageCustomRangeName: '',
    storageDatePeriodName: ''
  },
  defaultSelectIndex: 1,
  reportType: 0,
  Timezone: '0'
};

export default ReportsDatepickerModule;
